import {
  DEFAULT_SCHOOL_ZONE,
  type LocalDate,
  type SchoolHoliday,
  type SchoolHolidaysResponse,
  holidayOn,
} from '@ergo/shared';
import { api } from './api';
import { useResource } from './hooks';
import { addDays, formatDayMonthShort, isValidDate } from './time';

const NONE: SchoolHolidaysResponse = { zone: DEFAULT_SCHOOL_ZONE, holidays: [] };

/**
 * Les vacances scolaires de sa zone sur la fenêtre affichée. Une erreur ne remonte
 * pas : comme l'agenda perso, c'est un décor utile, jamais une raison d'abîmer
 * l'écran — Réglages est le seul endroit qui dit qu'une récupération a échoué.
 */
export function useSchoolHolidays(from: LocalDate, to: LocalDate): SchoolHoliday[] {
  const resource = useResource(
    () =>
      isValidDate(from) && isValidDate(to)
        ? api.holidays.list({ from, to }).catch(() => NONE)
        : Promise.resolve(NONE),
    [from, to],
  );
  return resource.data?.holidays ?? [];
}

/** Les périodes distinctes qui couvrent au moins un des jours donnés, dans l'ordre. */
export function holidaysOverDays(days: LocalDate[], holidays: SchoolHoliday[]): SchoolHoliday[] {
  return holidays.filter((holiday) => days.some((day) => holidayOn(day, [holiday]) !== null));
}

/**
 * Le dernier jour de vacances. La période s'arrête sur le jour de la rentrée, exclu :
 * c'est la veille qu'une phrase doit nommer.
 */
export function lastHolidayDay(holiday: SchoolHoliday): LocalDate {
  return addDays(holiday.end, -1);
}

/** « du 18 oct. au 2 nov. » */
export function holidayRangeLabel(holiday: SchoolHoliday): string {
  return `du ${formatDayMonthShort(holiday.start)} au ${formatDayMonthShort(lastHolidayDay(holiday))}`;
}

/** « jusqu’au 2 nov. » — quand on est déjà dedans. */
export function holidayUntilLabel(holiday: SchoolHoliday): string {
  return `jusqu’au ${formatDayMonthShort(lastHolidayDay(holiday))}`;
}
